'use client';

import { useParams, useRouter } from 'next/navigation';
import { FC, Key } from 'react';
import StudioPreviewLanguageSelector from '@/components/studio/studio-page-content/studio-preview/studio-preview-language-selector';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
} from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import useHeaderContent from '@/hooks/use-header-content';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { usePersistUserLocale } from '@/hooks/use-persist-user-locale';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import { setLocaleCookie } from '@/utils/locale-cookie';
import { resolveLocalizedPathname } from '@/utils/locale-href';

const LanguageSelectorLive: FC = () => {
  const resolveThemeColor = useResolveThemeColor();
  const router = useRouter();
  const params = useParams();
  const coreConfig = useCoreConfig();
  const headerContent = useHeaderContent();
  const { changeMyCartLocale } = useMyCart();
  const { changeFavoritesLocale } = useFavorites();
  const { isAuthenticated } = useKeycloak();
  const persistUserLocale = usePersistUserLocale();

  const currentLanguage = (
    (params?.locale as string | undefined) ??
    coreConfig.defaultLanguageCode ??
    'en'
  ).toLowerCase();

  const currentLanguageLabel =
    coreConfig.supportedLanguages.find(language => language.code.toLowerCase() === currentLanguage)
      ?.label ?? currentLanguage.toUpperCase();

  const handleChange = (key: Key) => {
    const newLocale = key.toString();
    if (isAuthenticated) {
      // `persistUserLocale` writes cookie + navigates inside `onMutate` (optimistic).
      persistUserLocale(newLocale);
    } else {
      setLocaleCookie(newLocale);
      const target = resolveLocalizedPathname(newLocale, coreConfig.defaultLanguageCode);
      const search = typeof window !== 'undefined' ? window.location.search : '';
      router.replace(`${target}${search}`);
    }
    changeMyCartLocale(newLocale);
    changeFavoritesLocale(newLocale);
  };

  const labelColor = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');

  if (coreConfig.supportedLanguages.length <= 1) {
    return null;
  }

  return (
    <Dropdown className="dropdown-custom" classNames={{ content: 'min-w-0' }}>
      <DropdownTrigger>
        <Button
          variant="light"
          aria-label={`Select language, current: ${currentLanguageLabel}`}
          className="min-w-0 p-2"
        >
          <div className={cn('flex', 'items-center', 'gap-2')}>
            <Image
              alt={`${currentLanguage} flag`}
              src={`https://flagcdn.com/${currentLanguage.toLowerCase() === 'en' ? 'gb' : currentLanguage.toLowerCase()}.svg`}
              width={20}
              height={15}
              style={{ borderRadius: 0 }}
            />
            <span style={{ color: labelColor }}>{currentLanguage.toUpperCase()}</span>
          </div>
        </Button>
      </DropdownTrigger>

      <DropdownMenu
        aria-label="Language Selector"
        className="dropdown-menu-custom"
        onAction={handleChange}
      >
        {coreConfig.supportedLanguages.map(language => (
          <DropdownItem
            key={language.code}
            value={language.code}
            textValue={language.label}
            className={cn(currentLanguage === language.code ? 'bg-gray-200' : 'bg-transparent')}
          >
            <div className={cn('flex items-center justify-start gap-2')}>
              <Image
                alt={`${language} flag`}
                src={`https://flagcdn.com/${language.code.toLowerCase() === 'en' ? 'gb' : language.code.toLowerCase()}.svg`}
                width={20}
                height={15}
                style={{ borderRadius: 0 }}
              />
              {language.label}
            </div>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

const LanguageSelector: FC = () => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  if (isStudioPreview) {
    return <StudioPreviewLanguageSelector />;
  }
  return <LanguageSelectorLive />;
};

export default LanguageSelector;
